import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { api } from '../services/api';
import type { TwinsPerUser, Match, GrowthData, MatchDistribution, AnalyticsOverview } from '../types';
import { Layout } from '../components/layout/Layout';
import { TwinsChart } from '../components/analytics/TwinsChart';
import { MatchesTable } from '../components/analytics/MatchesTable';
import { Card, CardHeader } from '../components/ui/Card';
import { StatCard } from '../components/analytics/StatCard';
import { useToast } from '../components/ui/Toast';
import { formatNumber } from '../utils/helpers';
import { Users, Bot, Heart, Activity, RefreshCw } from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  BarChart, Bar,
} from 'recharts';

const CHART_TOOLTIP_STYLE = {
  contentStyle: {
    backgroundColor: '#141420',
    border: '1px solid #252535',
    borderRadius: '12px',
    fontSize: '13px',
  },
  labelStyle: { color: '#f1f5f9' },
  cursor: { fill: 'rgba(255,255,255,0.04)' },
};

/** Upper bound on rows shown when Recent Matches is filtered by date. */
const RECENT_MATCHES_FILTERED_LIMIT = 200;

function SkeletonBlock({ h = 'h-[300px]' }: { h?: string }) {
  return <div className={`${h} animate-shimmer rounded-xl`} />;
}

export function Analytics() {
  const { toast } = useToast();
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [twinsPerUser, setTwinsPerUser] = useState<TwinsPerUser[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [growthData, setGrowthData] = useState<GrowthData[]>([]);
  const [matchDist, setMatchDist] = useState<MatchDistribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Recent Matches date filter. Only that table follows it - the stat cards
  // and charts keep showing all-time data.
  const [matchFrom, setMatchFrom] = useState('');
  const [matchTo, setMatchTo] = useState('');
  const [filteredResult, setFilteredResult] = useState<{ key: string; matches: Match[] } | null>(null);
  const filterKey = `${matchFrom}|${matchTo}`;
  const dateFiltered = Boolean(matchFrom || matchTo);
  const recentMatches = dateFiltered
    ? (filteredResult?.key === filterKey ? filteredResult.matches : [])
    : matches;
  const recentLoading = dateFiltered && filteredResult?.key !== filterKey;

  useEffect(() => {
    if (!matchFrom && !matchTo) return;
    let cancelled = false;
    const key = `${matchFrom}|${matchTo}`;
    api.getMatches(RECENT_MATCHES_FILTERED_LIMIT, {
      createdFrom: matchFrom || undefined,
      createdTo: matchTo || undefined,
    })
      .then(res => { if (!cancelled) setFilteredResult({ key, matches: res.matches }); })
      .catch(err => {
        if (cancelled) return;
        setFilteredResult({ key, matches: [] });
        toast(err instanceof Error ? err.message : 'Failed to load matches for these dates.', 'error');
      });
    return () => { cancelled = true; };
    // lastUpdated: the page's Refresh button re-runs the filtered query too.
  }, [matchFrom, matchTo, lastUpdated, toast]);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const [twinsData, matchesData, growthRes, overviewData, distData] = await Promise.all([
        api.getTwinsPerUser(),
        api.getMatches(20),
        api.getGrowthData(30),
        api.getAnalyticsOverview(),
        api.getMatchDistribution(),
      ]);
      setTwinsPerUser(twinsData.data);
      setMatches(matchesData.matches);
      setGrowthData(growthRes.data);
      setOverview(overviewData);
      setMatchDist(distData.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load analytics:', err);
      toast(err instanceof Error ? err.message : 'Failed to load analytics.', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Dashboard's "Total Matches" card links to /analytics#recent-matches. The
  // router doesn't scroll to hashes, and the table only exists after loading.
  useEffect(() => {
    if (loading || location.hash !== '#recent-matches') return;
    document.getElementById('recent-matches')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, location.hash]);

  const pieData = overview ? [
    { name: 'Company', value: overview.twinsByType.company, color: '#3b82f6' },
    { name: 'Job Seeker', value: overview.twinsByType.job_seeker, color: '#22c55e' },
  ] : [];

  const avgMatch = matches.length
    ? Math.round(matches.reduce((s, m) => s + m.matchPercentage, 0) / matches.length)
    : 0;

  const topByMatches = [...twinsPerUser]
    .sort((a, b) => b.twinCount - a.twinCount)
    .slice(0, 5);

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#f1f5f9]">Analytics</h2>
            <p className="text-sm text-[#64748b] mt-1">
              Detailed insights into platform usage and growth
            </p>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-xs text-[#64748b]">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#141420] border border-[#252535] text-sm text-[#94a3b8] hover:text-[#f1f5f9] hover:border-[#353548] transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Overview stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-[#141420] border border-[#252535] rounded-2xl p-5">
                <div className="space-y-3">
                  <div className="h-3 w-24 animate-shimmer rounded" />
                  <div className="h-8 w-20 animate-shimmer rounded" />
                  <div className="h-3 w-16 animate-shimmer rounded" />
                </div>
              </div>
            ))
          ) : overview ? (
            <>
              <StatCard title="Total Users"   value={overview.totalUsers}   icon={<Users    className="w-5 h-5" />} trend={12} trendLabel="vs last month" accent="blue"   />
              <StatCard title="Total Twins"   value={overview.totalTwins}   icon={<Bot      className="w-5 h-5" />} trend={8}  trendLabel="vs last month" accent="purple" />
              <StatCard title="Total Matches" value={overview.totalMatches} icon={<Heart    className="w-5 h-5" />} trend={24} trendLabel="vs last month" accent="red"    />
              <StatCard title="Active Twins"  value={overview.activeTwins}  icon={<Activity className="w-5 h-5" />} trend={5}  trendLabel="vs last month" accent="green"  />
            </>
          ) : null}
        </div>

        {/* Row 1: Twins per user + Twins by type */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {loading ? <SkeletonBlock h="h-[400px]" /> : <TwinsChart data={twinsPerUser} />}
          </div>

          <Card>
            <CardHeader title="Twins by Type" subtitle="Distribution of twin types" />
            {loading ? <SkeletonBlock /> : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={65} outerRadius={105}
                      paddingAngle={4} dataKey="value">
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip {...CHART_TOOLTIP_STYLE} />
                    <Legend
                      wrapperStyle={{ fontSize: '13px', color: '#94a3b8' }}
                      formatter={(value) => <span style={{ color: '#94a3b8' }}>{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>

        {/* Row 2: Growth + Match distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Growth trends */}
          <Card>
            <CardHeader title="Growth Trends" subtitle="Users & twins created over last 30 days" />
            {loading ? <SkeletonBlock /> : growthData.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-sm text-[#64748b]">
                No growth data for this period
              </div>
            ) : (
              <div className="h-[260px] mt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={growthData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#252535" vertical={false} />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip {...CHART_TOOLTIP_STYLE} />
                    <Legend wrapperStyle={{ fontSize: '12px' }}
                      formatter={(v) => <span style={{ color: '#94a3b8' }}>{v}</span>} />
                    <Line type="monotone" dataKey="users" stroke="#3b82f6" strokeWidth={2} dot={false} name="Users" />
                    <Line type="monotone" dataKey="twins" stroke="#22c55e" strokeWidth={2} dot={false} name="Twins" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          {/* Match percentage distribution */}
          <Card>
            <CardHeader title="Match Distribution" subtitle="How well twins are matching" />
            {loading ? <SkeletonBlock /> : (
              <div className="h-[260px] mt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={matchDist} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#252535" vertical={false} />
                    <XAxis dataKey="range" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip {...CHART_TOOLTIP_STYLE} itemStyle={{ color: '#a855f7' }} />
                    <Bar dataKey="count" name="Matches" fill="#a855f7" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>

        {/* Row 3: Top users + avg match */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top users by twins */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader
                title="Top Users by Twins"
                subtitle="Users with the most twins created"
                action={
                  <span className="text-xs text-[#64748b] bg-[#1c1c2a] px-2.5 py-1 rounded-lg border border-[#252535]">
                    Top 5
                  </span>
                }
              />
              {loading ? <SkeletonBlock h="h-[200px]" /> : (
                <div className="space-y-3 mt-1">
                  {topByMatches.map((user, i) => (
                    <div
                      key={user.userId}
                      className="flex items-center gap-4 cursor-pointer hover:bg-[#1c1c2a] rounded-xl px-2 -mx-2 py-1 transition-colors"
                      onClick={() => navigate(`/users/${user.userId}`)}
                    >
                      <span className="w-5 text-xs font-bold text-[#64748b] text-right shrink-0">
                        {i + 1}
                      </span>
                      <div className="w-8 h-8 rounded-xl gradient-blue flex items-center justify-center text-xs font-semibold text-white shrink-0">
                        {user.userName.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-[#f1f5f9] truncate">{user.userName}</span>
                          <span className="text-xs font-semibold text-[#94a3b8] ml-2 shrink-0">
                            {user.twinCount} twin{user.twinCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="h-1.5 bg-[#1c1c2a] rounded-full overflow-hidden">
                          <div
                            className="h-full gradient-blue rounded-full transition-all duration-500"
                            style={{
                              width: `${topByMatches[0].twinCount
                                ? (user.twinCount / topByMatches[0].twinCount) * 100
                                : 0}%`
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {!loading && topByMatches.length === 0 && (
                    <p className="text-sm text-[#64748b] text-center py-6">No data yet</p>
                  )}
                </div>
              )}
            </Card>
          </div>

          {/* Summary stats */}
          <div className="space-y-4">
            <Card className="text-center">
              <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Avg Match Score</p>
              {loading ? (
                <div className="h-10 w-20 animate-shimmer rounded mx-auto mt-3" />
              ) : (
                <p className="text-4xl font-bold mt-2 gradient-text-blue">{avgMatch}%</p>
              )}
              <p className="text-xs text-[#64748b] mt-1">across all matches</p>
            </Card>

            <Card className="text-center">
              <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Total Matches</p>
              {loading ? (
                <div className="h-10 w-20 animate-shimmer rounded mx-auto mt-3" />
              ) : (
                <p className="text-4xl font-bold mt-2 text-[#f1f5f9]">
                  {formatNumber(matches.length)}
                </p>
              )}
              <p className="text-xs text-[#64748b] mt-1">all time</p>
            </Card>

            <Card className="text-center">
              <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Active Rate</p>
              {loading || !overview ? (
                <div className="h-10 w-20 animate-shimmer rounded mx-auto mt-3" />
              ) : (
                <p className="text-4xl font-bold mt-2 text-[#22c55e]">
                  {overview.totalTwins
                    ? Math.round((overview.activeTwins / overview.totalTwins) * 100)
                    : 0}%
                </p>
              )}
              <p className="text-xs text-[#64748b] mt-1">twins currently active</p>
            </Card>
          </div>
        </div>

        {/* All matches table */}
        <div id="recent-matches" className="scroll-mt-6">
        <MatchesTable
          matches={recentMatches}
          showAll
          dateFilter={{
            from: matchFrom,
            to: matchTo,
            onFromChange: setMatchFrom,
            onToChange: setMatchTo,
            onClear: () => { setMatchFrom(''); setMatchTo(''); },
            loading: recentLoading,
          }}
        />
        </div>
      </div>
    </Layout>
  );
}
